import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, CardBody, Input, Button, Label, Spinner } from 'reactstrap';
import Header from '../../component/layout/Header';
import Footer from '../../component/layout/Footer';
import Page from '../../component/layout/Page';
import Title from '../../component/layout/Title';
import axios from 'axios';
import { mainurl } from '../../MainUrl';
import { toast } from 'react-toastify';
import { notification } from '../../component/hocs/notification';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const FinishedProductOut = () => {
  const navigate = useNavigate();

  // State variables
  const [finishedProductName, setFinishedProductName] = useState('');
  const [stockOutQuantity, setStockOutQuantity] = useState('');
  const [uom, setUom] = useState('');
  const [customerCode, setCustomerCode] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [customerList, setCustomerList] = useState([]);
  const [productList, setProductList] = useState([]); // Added product list state

  const [uomOptions] = useState([
    { id: 1, name: 'Kilogram' },
    { id: 2, name: 'Gram' },
    { id: 3, name: 'Piece' },
    { id: 4, name: 'Liter' },
    { id: 5, name: 'Milliliter' },
  ]);

  // Fetch data on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCustomerList();
    fetchProductList(); // Fetch product list when component mounts
  }, []);

  const fetchProductList = async () => {
    try {
      const response = await axios.get(mainurl + 'product_list');
      if (Array.isArray(response.data.data)) {
        setProductList(response.data.data);
      } else {
        console.error('Invalid product data structure');
      }
    } catch (err) {
      console.error('Error fetching finished product list:', err);
      notification({ type: 'error', message: 'Error while fetching finished product list', position: 'bottom' });
    }
  };

  // Fetch customer list from API
  const fetchCustomerList = async () => {
    try {
      const response = await axios.get(`${mainurl}view_customer`);
      if (Array.isArray(response.data.data)) {
        setCustomerList(response.data.data);
      } else {
        console.error('Invalid customer data structure');
      }
    } catch (err) {
      console.error('Error fetching customer list:', err);
      notification({ type: 'error', message: 'Error while fetching customer list', position: 'bottom' });
    }
  };

  const handleSubmit = () => {
    if (![finishedProductName, stockOutQuantity, uom, customerCode, transactionDate].every(Boolean)) {
      notification({ type: 'error', message: 'Please fill all the required fields', position: 'bottom' });
      return;
    }

    // Map selected UOM name to its corresponding uom_id
    const selectedUom = uomOptions.find((uomOption) => uomOption.name === uom);
    const uom_id = selectedUom ? selectedUom.id : null;

    if (!uom_id) {
      notification({ type: 'error', message: 'Invalid UOM selected', position: 'bottom' });
      return;
    }

    const payload = {
      finished_product_name: finishedProductName,
      stock_out: stockOutQuantity,
      uom_id: uom_id,  // Corrected: Sending uom_id instead of uom name
      customer_code: customerCode,
      transaction_date: transactionDate,
    };

    console.log('Input JSON:', JSON.stringify(payload, null, 2));

    setLoading(true);

    axios.post(mainurl + 'finished_out', payload)
      .then((response) => {
        setLoading(false);
        if (response.data.success === true || response.data.status === 1) {
          toast.success('Finished Product Stock Out successfully');
          setSuccessMessage('Stock Out Successful!');
          setFinishedProductName('');
          setStockOutQuantity('');
          setUom('');
          setCustomerCode('');
          setTransactionDate('');
        } else {
          notification({ type: 'error', message: 'Failed to save product out', position: 'bottom' });
        }
      })
      .catch(() => {
        setLoading(false);
        notification({ type: 'error', message: 'Error while saving product out', position: 'bottom' });
      });
  };

  return (
    <div>
      <Header />
      <Page>
        <Row className='mb-3'>
          <Col xs={12} className='mb-3'>
            <Title pageTitle="Finished Product Stock Out" />
          </Col>
          <Col xs={12}>
            <Card color="primary" outline className='mb-3'>
              <CardBody>
                <Row className='my-2'>
                  <Col md={6}>
                    <Label>Finished Product Name</Label>
                    <Input
                      type="select"
                      value={finishedProductName}
                      onChange={(e) => setFinishedProductName(e.target.value)}
                      required
                    >
                      <option value="">Select Finished Product</option>
                      {productList.map((product) => (
                        <option key={product.finished_product_id} value={product.finished_product_name}>
                          {product.finished_product_name}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col md={6}>
                    <Label>Customer Code</Label>
                    <Input
                      type="select"
                      value={customerCode}
                      onChange={(e) => setCustomerCode(e.target.value)}
                      required
                    >
                      <option value="">Select Customer Code</option>
                      {customerList.map((customer) => (
                        <option key={customer.customer_code} value={customer.customer_code}>
                          {customer.customer_code} - {customer.customer_name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                <Row className='my-2'>
                  <Col md={6}>
                    <Label>Stock Out Quantity</Label>
                    <Input
                      type="number"
                      value={stockOutQuantity}
                      onChange={(e) => setStockOutQuantity(e.target.value)}
                      placeholder="Stock Out Quantity"
                      required
                    />
                  </Col>

                  <Col md={6}>
                    <Label>Unit of Measurement (UOM)</Label>
                    <Input
                      type="select"
                      value={uom}
                      onChange={(e) => setUom(e.target.value)}
                      required
                    >
                      <option value="">Select UOM</option>
                      {uomOptions.map((uomOption) => (
                        <option key={uomOption.id} value={uomOption.name}>
                          {uomOption.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                <Row className='my-2'>
                  <Col md={6}>
                                        <Label>Transaction Date</Label>
                                        <DatePicker
  selected={transactionDate ? new Date(transactionDate) : null}
  onChange={(date) => setTransactionDate(date)}
  dateFormat="dd/MM/yyyy"
  placeholderText="Select a date"
  className="form-control"
  required
/>

                                      </Col>
                                    </Row>

                <Button
                  className="mt-3"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Save Product Out'}
                </Button>

                {successMessage && (
                  <div className="mt-3 text-success">
                    <strong>{successMessage}</strong>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Page>
      <Footer />
    </div>
  );
};

export default FinishedProductOut;
